import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { usePermissions, PERMISSIONS } from '../hooks/usePermissions';
import { useToast } from '../hooks/useToast';
import {
  useIngredientes,
  useIngredientesEstadisticas,
  useCreateIngrediente,
  useUpdateIngrediente,
  useDeshabilitarIngrediente,
  useReactivarIngrediente,
  useAjustarStock,
  useLotesIngrediente,
} from '../hooks/useIngredientes';
import { useCreateMerma } from '../hooks/useMermas';
import { useCategorias } from '../hooks/useCategorias';

import PageHeader from '../components/PageHeader';
import IngredienteForm from '../components/IngredienteForm';
import OrdenCompraForm from '../components/OrdenCompraForm';
import ModalMermaRapida from '../components/ModalMermaRapida';
import ModalAjusteStock from '../components/ModalAjusteStock';
import TablaMovimientosStock from '../components/TablaMovimientosStock';
import DetalleOrdenCompra from '../components/DetalleOrdenCompra';
import IngredienteLotesPanel from '../components/IngredienteLotesPanel';
import WarningsUnidadesBanner from '../components/WarningsUnidadesBanner';
import VistaBodega from '../components/VistaBodega';
import {
  Package,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  Calendar,
  Truck,
  TrendingDown,
  Eye,
  Edit,
  Trash2,
  Download,
  X,
  Clock,
  DollarSign,
  Info,
  ChevronDown,
  ChevronUp,
  PowerOff,
  Sliders,
  Boxes,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import type {
  Ingrediente,
  FiltrosIngredientes,
  EstadisticasInventario,
  AlertaStock,
} from '../types/ingredientes';

const Ingredientes: React.FC = () => {
  const { user } = useAuth();
  const { success, warning, error: showError } = useToast();
  const queryClient = useQueryClient();

  // Estados de filtros y UI
  const [filtros, setFiltros] = useState<FiltrosIngredientes>({
    page: 1,
    limit: 10,
  });
  const [alertas, setAlertas] = useState<AlertaStock[]>([]);
  const [vistaActiva, setVistaActiva] = useState<'gestion' | 'bodega'>('gestion');

  // Hooks de React Query para datos del backend
  // Mapear filtros del componente a filtros del API.
  //
  // 2026-06-16 — fix bug "filtro por críticos no filtra". El select
  // "Estado de Stock" (crítico/bajo/normal) estaba sin handler. Lo
  // conectamos a `filtros.estadoStock`. Como el backend solo expone
  // `stock_bajo` (= stock<=minimo, equivalente a "crítico"), las
  // opciones "bajo" y "normal" se filtran en cliente. Para evitar
  // inconsistencia con la paginación cuando hay filtro local
  // activo, pedimos al backend SIN paginación (limit=1000).
  const filtroEstadoActivo = !!filtros.estadoStock;
  const apiFilters = {
    page: filtroEstadoActivo ? 1 : filtros.page,
    limit: filtroEstadoActivo ? 1000 : filtros.limit,
    search: filtros.busqueda, // Mapeo: busqueda → search
    categoria: filtros.categoria,
    activo: filtros.activos,
    // El backend filtra críticos vía stock_bajo. Para "bajo" y
    // "normal" no hay parámetro equivalente, así que en esos casos
    // traemos todos y filtramos local.
    stock_bajo:
      filtros.stockBajo || filtros.estadoStock === 'critico' || undefined,
  };

  const {
    data: ingredientesData,
    isLoading: loading,
    error: ingredientesError,
  } = useIngredientes(apiFilters);

  // Hook para obtener estadísticas generales (todos los ingredientes, no paginados)
  const { data: estadisticasData } = useIngredientesEstadisticas();

  // Categorías para el filtro de la lista (reemplaza opciones hardcoded
  // falsas que tenía el select decorativo previo).
  const { data: categoriasData } = useCategorias();

  // Mutations para CRUD
  const createMutation = useCreateIngrediente();
  const updateMutation = useUpdateIngrediente();

  // Mutations para nuevas funcionalidades
  const deshabilitarMutation = useDeshabilitarIngrediente();
  const reactivarMutation = useReactivarIngrediente();
  const ajustarStockMutation = useAjustarStock();

  // Mutation para mermas
  const mermaRapidaMutation = useCreateMerma();

  // Extraer los ingredientes del resultado de la API
  const ingredientes = ingredientesData?.ingredientes || [];
  const pagination = ingredientesData?.pagination;

  // Mostrar error si existe
  if (ingredientesError) {
    console.error('Error loading ingredientes:', ingredientesError);
  }

  // Datos de paginación con valores por defecto
  const paginationInfo = {
    page: pagination?.page || 1,
    limit: pagination?.limit || 10,
    total: pagination?.total || 0,
    pages: pagination?.pages || 0,
  };

  // Calcular el rango de elementos mostrados
  const startItem =
    paginationInfo.total === 0
      ? 0
      : (paginationInfo.page - 1) * paginationInfo.limit + 1;
  const endItem = Math.min(
    paginationInfo.page * paginationInfo.limit,
    paginationInfo.total
  );

  // Función para cambiar página
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= paginationInfo.pages) {
      setFiltros((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePageSizeChange = (newLimit: number) => {
    setFiltros((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Estados para el formulario modal
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [ingredienteSeleccionado, setIngredienteSeleccionado] =
    useState<Ingrediente | null>(null);
  const [modoFormulario, setModoFormulario] = useState<'crear' | 'editar'>(
    'crear'
  );
  const [showFiltros, setShowFiltros] = useState(false);
  const [showOrdenForm, setShowOrdenForm] = useState(false);
  const [showDetalles, setShowDetalles] = useState(false);
  const [ingredienteDetalles, setIngredienteDetalles] =
    useState<Ingrediente | null>(null);

  // Estados para el modal de merma
  const [showModalMerma, setShowModalMerma] = useState(false);
  const [ingredienteParaMerma, setIngredienteParaMerma] =
    useState<Ingrediente | null>(null);

  // Estado para el modal de ajuste de stock
  const [showModalAjuste, setShowModalAjuste] = useState(false);
  const [ingredienteParaAjuste, setIngredienteParaAjuste] =
    useState<Ingrediente | null>(null);

  // Estado para colapsar/expandir alertas (colapsado por defecto)
  const [alertasColapsadas, setAlertasColapsadas] = useState(true);

  // Estado para el modal de detalle de orden de compra
  const [showDetalleOrden, setShowDetalleOrden] = useState(false);
  const [ordenSeleccionadaId, setOrdenSeleccionadaId] = useState<string>('');

  // Estados para el modal de error
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState<
    'simple' | 'similar' | 'exacto' | 'exactoActivo'
  >('simple');
  const [ingredientesSimilares, setIngredientesSimilares] = useState<any[]>([]);
  const [ingredienteExacto, setIngredienteExacto] = useState<any | null>(null);
  const [ingredienteDataPendiente, setIngredienteDataPendiente] =
    useState<Partial<Ingrediente> | null>(null);

  // Permisos dinámicos (tabla roles_permisos), no rol hardcodeado: así el rol
  // `encargado_inventario` obtiene las mismas acciones sin tocar este archivo.
  const { hasPermission } = usePermissions();
  const puedeCrear = hasPermission(PERMISSIONS.INGREDIENTES_CREATE);
  const puedeEditar = hasPermission(PERMISSIONS.INGREDIENTES_UPDATE);
  const puedeEliminar = hasPermission(PERMISSIONS.INGREDIENTES_DELETE);
  const puedeComprar = hasPermission(PERMISSIONS.COMPRAS_CREATE);
  // El ajuste manual de stock sigue restringido a administrador por decisión
  // explícita (2026-09-08): ese endpoint escribe sólo `stock_actual` y el
  // trigger trg_ingredientes_sync_legacy_before_update lo recalcula desde la
  // columna base, así que hoy no surte efecto real.
  const esAdmin = user?.rol === 'administrador';

  // Funciones para el formulario
  const abrirFormularioCrear = () => {
    setIngredienteSeleccionado(null);
    setModoFormulario('crear');
    setMostrarFormulario(true);
  };

  const abrirFormularioEditar = (ingrediente: Ingrediente) => {
    setIngredienteSeleccionado(ingrediente);
    setModoFormulario('editar');
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setIngredienteSeleccionado(null);
  };

  const abrirDetallesIngrediente = (ingrediente: Ingrediente) => {
    setIngredienteDetalles(ingrediente);
    setShowDetalles(true);
  };

  const cerrarDetalles = () => {
    setShowDetalles(false);
    setIngredienteDetalles(null);
  };

  const handleGuardarIngrediente = async (
    ingredienteData: Partial<Ingrediente>
  ) => {
    try {
      if (modoFormulario === 'crear') {
        const creado = await createMutation.mutateAsync(ingredienteData);
        success('Ingrediente creado exitosamente');
        // El backend avisa cuando no pudo resolver la unidad contra el
        // catalogo: el ingrediente queda creado pero fuera de las conversiones
        // y del consumo por lotes en unidad base. Se muestra aparte del exito
        // y con mas duracion, porque exige una correccion posterior.
        if (creado?.advertenciaUnidad) {
          warning('Unidad no reconocida', creado.advertenciaUnidad, 12000);
        }
      } else if (ingredienteSeleccionado) {
        const id = parseInt(ingredienteSeleccionado.id);
        await updateMutation.mutateAsync({
          id,
          data: ingredienteData,
        });
        success('Ingrediente actualizado exitosamente');
      }
      cerrarFormulario();
    } catch (error: any) {
      console.error('Error al guardar ingrediente:', error);

      // Verificar el tipo de error por el status code
      if (error?.response?.status === 400) {
        // Error de ingrediente exacto activo
        const exacto = error.response.data?.ingrediente_existente || null;
        setIngredienteExacto(exacto);
        setIngredientesSimilares([]);
        setIngredienteDataPendiente(null); // No permitir continuar
        setErrorType('exactoActivo');
        setErrorMessage(
          error.response.data?.error ||
            'Ya existe un ingrediente activo con ese nombre'
        );
      } else if (error?.response?.status === 409) {
        // Error de ingredientes similares - guardar datos para usar después
        const similares = error.response.data?.ingredientes_similares || [];
        setIngredientesSimilares(similares);
        setIngredienteExacto(null);
        setIngredienteDataPendiente(ingredienteData); // Guardar datos para continuar después
        setErrorType('similar');
        setErrorMessage(
          error.response.data?.error || 'Se encontraron ingredientes similares'
        );
      } else if (error?.response?.status === 410) {
        // Error de ingrediente exacto inactivo
        const exacto = error.response.data?.ingrediente_exacto || null;
        setIngredienteExacto(exacto);
        setIngredientesSimilares([]);
        setIngredienteDataPendiente(ingredienteData);
        setErrorType('exacto');
        setErrorMessage(
          error.response.data?.error ||
            'Existe un ingrediente con el mismo nombre pero está inactivo'
        );
      } else {
        // Error simple (otros)
        setErrorType('simple');
        setIngredientesSimilares([]);
        setIngredienteExacto(null);
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          'Error al guardar el ingrediente';
        setErrorMessage(errorMessage);
      }

      setShowErrorModal(true);
    }
  };

  const handleEliminarIngrediente = (ingrediente: Ingrediente) => {
    // En lugar de eliminar, abrimos el modal de merma
    setIngredienteParaMerma(ingrediente);
    setShowModalMerma(true);
  };

  // Funciones para Orden de Compra
  const handleGenerarOrden = () => {
    setShowOrdenForm(true);
  };

  const handleGuardarOrden = async (ordenData: any) => {
    try {
      // Importar el servicio si no está importado
      const { default: ordenesCompraAPI } = await import(
        '../services/ordenesCompraAPI'
      );

      // Generar número de orden con timestamp
      const ahora = new Date();
      const numero_orden = `OC-${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}-${String(ahora.getHours()).padStart(2, '0')}${String(ahora.getMinutes()).padStart(2, '0')}${String(ahora.getSeconds()).padStart(2, '0')}`;

      // Transformar datos del formulario al formato del backend
      const datosBackend = {
        numero_orden, // REQUERIDO: Auto-generado
        proveedor_id: ordenData.proveedorId
          ? parseInt(ordenData.proveedorId)
          : 1, // Fallback a "Proveedor Desconocido" si no se selecciona uno
        fecha_solicitud: ahora.toISOString().split('T')[0], // REQUERIDO: Fecha de hoy
        fecha_entrega_esperada: ordenData.fechaEsperada
          ? new Date(ordenData.fechaEsperada).toISOString().split('T')[0]
          : undefined,
        observaciones: ordenData.observaciones || '',
        solicitado_por: user?.id || 1, // REQUERIDO: ID del usuario autenticado
        detalles: ordenData.items.map((item: any) => ({
          // REQUERIDO: Renombrado de 'items' a 'detalles'
          ingrediente_id: parseInt(item.ingredienteId),
          cantidad_solicitada: parseFloat(item.cantidad),
          precio_unitario: parseFloat(item.precioUnitario),
          observaciones: item.observaciones || '',
        })),
      };

      // Crear la orden de compra
      const resultado = await ordenesCompraAPI.crear(datosBackend);

      // Invalidar cache de órdenes de compra para que se actualice la lista automáticamente
      queryClient.invalidateQueries({ queryKey: ['ordenes-compra'] });

      // Mostrar mensaje de éxito
      success(`Orden de compra ${resultado.data.numero} creada exitosamente`);

      // Cerrar el modal
      setShowOrdenForm(false);

      // Opcional: Podrías redirigir a la página de compras
      // navigate('/compras');
    } catch (error: any) {
      console.error('Error al crear orden de compra:', error);
      showError(
        error.response?.data?.message ||
          'Error al crear la orden de compra. Por favor, intente nuevamente.'
      );
    }
  };

  const handleCerrarOrdenForm = () => {
    setShowOrdenForm(false);
  };

  // Función para manejar la merma rápida
  const handleRegistrarMerma = async (datos: any) => {
    console.log('Registrando merma:', datos);

    try {
      // Usar la mutation para registrar la merma
      // Nota: lote_id se establece en 0 para mermas rápidas sin lote específico
      await mermaRapidaMutation.mutateAsync({
        lote_id: datos.loteId || 0, // 0 indica merma sin lote específico
        ingrediente_id: parseInt(datos.ingredienteId),
        cantidad: datos.cantidad,
        motivo: datos.motivo,
        observaciones: datos.observaciones,
      });

      // Cerrar el modal después del éxito
      handleCerrarModalMerma();
    } catch (error) {
      console.error('Error al registrar merma:', error);
      // El error ya se maneja en el hook useMermas
    }
  };

  const handleCerrarModalMerma = () => {
    setShowModalMerma(false);
    setIngredienteParaMerma(null);
  };

  // Funciones para manejar el modal de ajuste de stock
  const handleAbrirModalAjuste = (ingrediente: Ingrediente) => {
    setIngredienteParaAjuste(ingrediente);
    setShowModalAjuste(true);
  };

  const handleCerrarModalAjuste = () => {
    setShowModalAjuste(false);
    setIngredienteParaAjuste(null);
  };

  const handleConfirmarAjuste = async (data: {
    stock_nuevo: number;
    motivo: string;
    notas?: string;
  }) => {
    if (!ingredienteParaAjuste) return;

    try {
      const resultado = await ajustarStockMutation.mutateAsync({
        id: parseInt(ingredienteParaAjuste.id),
        ...data,
      });

      success(
        `Stock ajustado correctamente: ${resultado.movimiento.stock_anterior} → ${resultado.movimiento.stock_nuevo} ${ingredienteParaAjuste.unidadMedida.abreviacion}`
      );

      handleCerrarModalAjuste();
    } catch (error: any) {
      console.error('Error al ajustar stock:', error);
      showError(
        error.response?.data?.message ||
          'Error al ajustar el stock. Por favor, intente nuevamente.'
      );
    }
  };

  // Funciones para manejar el modal de detalle de orden
  const handleVerOrdenCompra = (ordenId: string) => {
    setOrdenSeleccionadaId(ordenId);
    setShowDetalleOrden(true);
  };

  const handleCerrarDetalleOrden = () => {
    setShowDetalleOrden(false);
    setOrdenSeleccionadaId('');
  };

  // Función para cerrar modal de error
  const handleCerrarModalError = () => {
    setShowErrorModal(false);
    setErrorMessage('');
    setErrorType('simple');
    setIngredientesSimilares([]);
    setIngredienteExacto(null);
    setIngredienteDataPendiente(null);
  };

  // Función para continuar creando ingrediente ignorando similares
  const handleContinuarDeTodasFormas = async () => {
    if (!ingredienteDataPendiente) return;

    try {
      // Agregar un flag para indicar que queremos continuar de todas formas
      const dataConFlag = {
        ...ingredienteDataPendiente,
        forzarCreacion: true,
      };

      if (modoFormulario === 'crear') {
        await createMutation.mutateAsync(dataConFlag);
        success('Ingrediente creado exitosamente');
      } else if (ingredienteSeleccionado) {
        const id = parseInt(ingredienteSeleccionado.id);
        await updateMutation.mutateAsync({
          id,
          data: dataConFlag,
        });
        success('Ingrediente actualizado exitosamente');
      }

      handleCerrarModalError();
      cerrarFormulario();
    } catch (error) {
      console.error('Error al forzar creación:', error);
      // Si aún hay error, cerrar modal pero mantener formulario abierto
      handleCerrarModalError();
    }
  };

  // Función para deshabilitar ingrediente con stock 0
  const handleDeshabilitarIngrediente = async (ingrediente: Ingrediente) => {
    if (ingrediente.stockActual > 0) {
      success('No se puede deshabilitar un ingrediente con stock mayor a 0');
      return;
    }

    try {
      await deshabilitarMutation.mutateAsync(Number(ingrediente.id));
      success(
        `Ingrediente "${ingrediente.nombre}" deshabilitado correctamente`
      );
    } catch (error) {
      console.error('Error al deshabilitar ingrediente:', error);
    }
  };

  // Función para reactivar ingrediente
  const handleReactivarIngrediente = async (
    ingrediente: Ingrediente,
    stockActual: number
  ) => {
    if (stockActual <= 0) {
      success('Se requiere un stock mayor a 0 para reactivar');
      return;
    }

    try {
      await reactivarMutation.mutateAsync({
        id: Number(ingrediente.id),
        stockActual,
      });
      success(`Ingrediente "${ingrediente.nombre}" reactivado correctamente`);
    } catch (error) {
      console.error('Error al reactivar ingrediente:', error);
    }
  };

  // Usar estadísticas del backend en lugar de calcularlas localmente
  const estadisticas: EstadisticasInventario | null = useMemo(() => {
    if (!estadisticasData) return null;

    return {
      // Fix tanda1 H6 (2026-06-30): la tarjeta "Total" mostraba
      // `totalIngredientes` (cuenta TODOS incluso `activo=0`) mientras
      // la lista pagina `paginationInfo.total` filtrado por activos.
      // Diferencia confirmada en BD: 353 totales vs 330 activos.
      // Criterio correcto operacional = ingredientes activos.
      totalIngredientes: estadisticasData.ingredientesActivos,
      ingredientesActivos: estadisticasData.ingredientesActivos,
      alertasStockMinimo: estadisticasData.alertasStockMinimo,
      alertasVencimiento: 0, // Por implementar
      // Fix tanda1 H7 (2026-06-30): se calculaba como literal 0. Ahora
      // el backend devuelve el SUM(stock_actual_base * costo_unitario)
      // sobre ingredientes activos con costo cargado (ver
      // db-helpers.ts getStatistics). Si el endpoint no envía el campo
      // (compat backward), fallback a 0.
      valorTotalInventario: (estadisticasData as { valorTotalInventario?: number }).valorTotalInventario ?? 0,
      ingredientesSinCosto: (estadisticasData as { ingredientesSinCosto?: number }).ingredientesSinCosto ?? 0,
      categoriasCount: estadisticasData.categoriasCount,
      movimientosUltimos30Dias: 0, // Por implementar
    };
  }, [estadisticasData]);

  // Calcular alertas usando useMemo (basado en ingredientes de la página actual para mostrar)
  const alertasCalculadas = useMemo(() => {
    if (!ingredientes.length) return [];

    return ingredientes
      .filter((ing: Ingrediente) => ing.stockActual <= ing.stockMinimo)
      .map((ing: Ingrediente, index: number) => ({
        id: `${index + 1}`,
        ingredienteId: ing.id,
        ingrediente: ing,
        tipo: 'stock_minimo' as const,
        mensaje: `${ing.nombre} - Stock bajo (${ing.stockActual} ${ing.unidadMedida.abreviacion} restantes)`,
        nivel: 'warning' as const,
        leida: false,
        fechaCreacion: new Date(),
      }));
  }, [ingredientes]);

  // Actualizar alertas cuando cambien
  useEffect(() => {
    setAlertas(alertasCalculadas);
  }, [alertasCalculadas]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  // Filtrado local por estado de stock cuando el filtro está activo.
  // - 'critico' = stock_actual <= stock_minimo (el backend ya filtró
  //   esto vía stock_bajo, pero re-aplicamos para que el sufijo
  //   "bajo" abajo NO arrastre criticos).
  // - 'bajo'    = stock_minimo < stock_actual <= stock_minimo * 1.5
  //   (zona amarilla).
  // - 'normal'  = stock_actual > stock_minimo * 1.5.
  // Si filtros.estadoStock es undefined, devolvemos todos.
  const ingredientesFiltrados = (() => {
    if (!filtros.estadoStock) return ingredientes;
    return ingredientes.filter((ing: Ingrediente) => {
      const min = ing.stockMinimo;
      const actual = ing.stockActual;
      if (filtros.estadoStock === 'critico') return actual <= min;
      if (filtros.estadoStock === 'bajo')
        return actual > min && actual <= min * 1.5;
      if (filtros.estadoStock === 'normal') return actual > min * 1.5;
      return true;
    });
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 relative"
      style={{
        backgroundImage: "url('/bg_dash.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/85 dark:bg-gray-900/85"></div>

      <PageHeader
        title="Gestión de Ingredientes"
        helpSeccion="ingredientes"
        subtitle="Control de inventario, stock y proveedores"
        actions={
          puedeCrear || puedeComprar ? (
            <div className="flex items-center space-x-3">
              {puedeComprar && (
                <button
                  onClick={handleGenerarOrden}
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
                >
                  <Truck className="h-5 w-5 mr-2" />
                  Generar Orden
                </button>
              )}
              {puedeCrear && (
                <button
                  onClick={abrirFormularioCrear}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Nuevo Ingrediente
                </button>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Contenido */}
      <div className="relative z-10 p-6">
        <WarningsUnidadesBanner />

        {/* Toggle Gestión ↔ Bodega */}
        <div className="mb-6">
          <div className="inline-flex p-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
            <button
              onClick={() => setVistaActiva('gestion')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                vistaActiva === 'gestion'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Package className="h-4 w-4 mr-2" />
              Gestión de ingredientes
            </button>
            <button
              onClick={() => setVistaActiva('bodega')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                vistaActiva === 'bodega'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Boxes className="h-4 w-4 mr-2" />
              Vista bodega (FIFO)
            </button>
          </div>
        </div>

        {vistaActiva === 'bodega' && (
          <div className="mb-8">
            <VistaBodega />
          </div>
        )}

        <div style={{ display: vistaActiva === 'gestion' ? 'block' : 'none' }}>
        <div className="mb-8">
          {/* Estadísticas */}
          {estadisticas && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Ingredientes
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {estadisticas.totalIngredientes}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Alertas Stock
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {estadisticas.alertasStockMinimo}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
                    <Calendar className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Próximos a Vencer
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {estadisticas.alertasVencimiento}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                    <TrendingDown className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Valor Total
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(estadisticas.valorTotalInventario)}
                    </p>
                    {/* Fix tanda1 H7 (2026-06-30): si hay activos sin
                        costo cargado, el valor está subestimado. Lo
                        avisamos en vez de mostrar $0 silencioso o un
                        número engañoso. */}
                    {estadisticas.ingredientesSinCosto && estadisticas.ingredientesSinCosto > 0 ? (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ⚠ {estadisticas.ingredientesSinCosto} ingrediente
                        {estadisticas.ingredientesSinCosto === 1 ? '' : 's'} sin costo cargado — no sumados
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Alertas */}
          {alertas.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 mb-6">
              <div className="flex items-center mb-4">
                <button
                  onClick={() => setAlertasColapsadas(!alertasColapsadas)}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mr-2"
                  title={
                    alertasColapsadas ? 'Expandir alertas' : 'Colapsar alertas'
                  }
                >
                  {alertasColapsadas ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Alertas Activas ({alertas.length})
                </h3>
              </div>

              {!alertasColapsadas && (
                <div className="space-y-3">
                  {alertas.map((alerta) => (
                    <div
                      key={alerta.id}
                      className={`p-3 rounded-lg border-l-4 ${
                        alerta.nivel === 'error'
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-400'
                          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {alerta.mensaje}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtros y Búsqueda */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar ingredientes..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  value={filtros.busqueda || ''}
                  onChange={(e) =>
                    setFiltros({
                      ...filtros,
                      busqueda: e.target.value,
                      page: 1,
                    })
                  }
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFiltros(!showFiltros)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <Filter className="h-5 w-5 mr-2" />
                Filtros
              </button>

              <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <Download className="h-5 w-5 mr-2" />
                Exportar
              </button>
            </div>
          </div>

          {/* Filtros expandidos */}
          {showFiltros && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoría
                  </label>
                  <select
                    value={filtros.categoria || ''}
                    onChange={(e) =>
                      setFiltros({
                        ...filtros,
                        categoria: e.target.value || undefined,
                        page: 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Todas</option>
                    {(categoriasData || []).map((cat: { id: number; nombre: string }) => (
                      // El backend filtra por nombre de categoría (cg.nombre = ?),
                      // así que el value debe ser el nombre, no el id.
                      <option key={cat.id} value={cat.nombre}>
                        {cat.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estado de Stock
                  </label>
                  <select
                    value={filtros.estadoStock || ''}
                    onChange={(e) =>
                      setFiltros({
                        ...filtros,
                        estadoStock:
                          (e.target.value as
                            | 'critico'
                            | 'bajo'
                            | 'normal'
                            | '') || undefined,
                        page: 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Todos</option>
                    <option value="normal">Stock Normal</option>
                    <option value="bajo">Stock Bajo</option>
                    <option value="critico">Stock Crítico</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Proveedor{' '}
                    <span className="text-xs font-normal italic text-gray-400">
                      (próximamente)
                    </span>
                  </label>
                  {/* Filtro por proveedor: pendiente de implementar
                      en el backend (no hay endpoint que cruce
                      ingredientes con su proveedor habitual). Lo
                      dejamos deshabilitado en vez de mostrar opciones
                      hardcoded falsas. TD-049. */}
                  <select
                    disabled
                    title="Próximamente — filtro por proveedor habitual (TD-049)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700/60 dark:text-gray-500 cursor-not-allowed opacity-60"
                  >
                    <option value="">Filtrar por proveedor — próximamente</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de Ingredientes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Lista de ingredientes
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                {filtros.estadoStock
                  ? `${ingredientesFiltrados.length} ${
                      filtros.estadoStock === 'critico'
                        ? 'crítico'
                        : filtros.estadoStock === 'bajo'
                          ? 'con stock bajo'
                          : 'con stock normal'
                    }${ingredientesFiltrados.length !== 1 ? 's' : ''} de ${paginationInfo.total}`
                  : `${paginationInfo.total} en total`}
              </span>
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3">Ingrediente</th>
                  <th className="px-4 py-3">Marca</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3 text-right">Stock actual</th>
                  <th className="px-4 py-3 text-right">Stock mínimo</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {ingredientesFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="text-gray-500 dark:text-gray-400">
                        <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium mb-1">
                          {filtros.estadoStock === 'critico'
                            ? 'No hay ingredientes críticos'
                            : filtros.estadoStock === 'bajo'
                              ? 'No hay ingredientes con stock bajo'
                              : filtros.estadoStock === 'normal'
                                ? 'No hay ingredientes con stock normal'
                                : 'No hay ingredientes'}
                        </p>
                        <p className="text-xs">
                          {filtros.estadoStock
                            ? 'Probá cambiar el filtro de estado a "Todos" para ver el resto.'
                            : filtros.busqueda
                              ? 'No se encontraron ingredientes con ese criterio de búsqueda.'
                              : 'Comienza agregando tu primer ingrediente.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  ingredientesFiltrados.map((ingrediente: Ingrediente) => {
                    const estadoStock =
                      ingrediente.stockActual <= ingrediente.stockMinimo
                        ? 'critico'
                        : ingrediente.stockActual <=
                            ingrediente.stockMinimo * 1.5
                          ? 'bajo'
                          : 'ok';
                    const badgeStock =
                      estadoStock === 'critico'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        : estadoStock === 'bajo'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
                    const labelStock =
                      estadoStock === 'critico'
                        ? 'Crítico'
                        : estadoStock === 'bajo'
                          ? 'Bajo'
                          : 'OK';
                    return (
                      <tr
                        key={ingrediente.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {ingrediente.nombre}
                          </div>
                          {ingrediente.descripcion && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ingrediente.descripcion}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {ingrediente.marca
                            ? ingrediente.marca.charAt(0).toUpperCase() +
                              ingrediente.marca.slice(1).toLowerCase()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {ingrediente.categoria}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {ingrediente.stockActual}{' '}
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            {ingrediente.unidadMedida.abreviacion}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                          {ingrediente.stockMinimo}{' '}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ingrediente.unidadMedida.abreviacion}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${badgeStock}`}
                          >
                            {labelStock}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              title="Ver detalles"
                              onClick={() =>
                                abrirDetallesIngrediente(ingrediente)
                              }
                              className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {/* Ajuste manual de stock: sólo administrador
                                (ver comentario en la definición de esAdmin). */}
                            {esAdmin && (
                              <button
                                title="Ajustar stock manualmente"
                                onClick={() =>
                                  handleAbrirModalAjuste(ingrediente)
                                }
                                className="p-1.5 rounded-md text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 transition-colors"
                              >
                                <Sliders className="h-4 w-4" />
                              </button>
                            )}
                            {puedeEditar && (
                              <button
                                title="Editar ingrediente"
                                onClick={() =>
                                  abrirFormularioEditar(ingrediente)
                                }
                                className="p-1.5 rounded-md text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {puedeEliminar && ingrediente.stockActual === 0 && (
                              <button
                                title="Deshabilitar (stock en 0)"
                                onClick={() =>
                                  handleDeshabilitarIngrediente(ingrediente)
                                }
                                className="p-1.5 rounded-md text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20 transition-colors"
                              >
                                <PowerOff className="h-4 w-4" />
                              </button>
                            )}
                            {puedeEliminar && (
                              <button
                                title="Eliminar ingrediente"
                                onClick={() =>
                                  handleEliminarIngrediente(ingrediente)
                                }
                                className="p-1.5 rounded-md text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {paginationInfo.total > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>Mostrando</span>
                <select
                  value={paginationInfo.limit}
                  onChange={(e) =>
                    handlePageSizeChange(parseInt(e.target.value))
                  }
                  className="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>
                  {paginationInfo.total === 0
                    ? '0 ingredientes'
                    : `${startItem}–${endItem} de ${paginationInfo.total} ingredientes`}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={paginationInfo.page === 1}
                  className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Primera página"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(paginationInfo.page - 1)}
                  disabled={paginationInfo.page <= 1}
                  className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Página anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  Página {paginationInfo.page} de {paginationInfo.pages || 1}
                </span>
                <button
                  onClick={() => handlePageChange(paginationInfo.page + 1)}
                  disabled={paginationInfo.page >= paginationInfo.pages}
                  className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Página siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(paginationInfo.pages)}
                  disabled={paginationInfo.page >= paginationInfo.pages}
                  className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Última página"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
        </div>

      {/* Modal del formulario de ingredientes */}
      <IngredienteForm
        isOpen={mostrarFormulario}
        onClose={cerrarFormulario}
        onSave={handleGuardarIngrediente}
        ingrediente={ingredienteSeleccionado}
        modo={modoFormulario}
      />

      {/* Modal del formulario de orden de compra */}
      <OrdenCompraForm
        isOpen={showOrdenForm}
        onClose={handleCerrarOrdenForm}
        onSave={handleGuardarOrden}
        modo="crear"
        sugerenciasIniciales={
          // Generar sugerencias basadas en ingredientes con stock bajo
          ingredientesFiltrados
            .filter((ing: Ingrediente) => ing.stockActual < ing.stockMinimo)
            .map((ing: Ingrediente) => ({
              ingredienteId: ing.id,
              ingrediente: {
                id: ing.id,
                nombre: ing.nombre,
                stockActual: ing.stockActual,
                stockMinimo: ing.stockMinimo,
                unidadMedida: { abreviacion: ing.unidadMedida.abreviacion },
              },
              cantidadSugerida: Math.max(
                ing.stockMinimo - ing.stockActual,
                ing.stockMinimo
              ),
              prioridad:
                ing.stockActual <= ing.stockMinimo * 0.5 ? 'alta' : 'media',
              razon: ing.stockActual <= 0 ? 'Sin stock' : 'Stock bajo',
              diasParaAgotamiento:
                Math.ceil(ing.stockActual / (ing.stockMinimo * 0.1)) || 0,
              proveedorSugerido: ing.proveedores?.[0]
                ? {
                    id: ing.proveedores[0].proveedor.id,
                    nombre: ing.proveedores[0].proveedor.nombre,
                    precioUnitario: ing.proveedores[0].precioUnitario,
                  }
                : undefined,
            }))
        }
      />

      {/* Modal de Detalles del Ingrediente */}
      {showDetalles && ingredienteDetalles && (
        <DetallesIngredienteModal
          ingrediente={ingredienteDetalles}
          isOpen={showDetalles}
          onClose={cerrarDetalles}
          onVerOrdenCompra={handleVerOrdenCompra}
        />
      )}

      {/* Modal de Merma Rápida */}
      <ModalMermaRapida
        isOpen={showModalMerma}
        onClose={handleCerrarModalMerma}
        ingrediente={ingredienteParaMerma}
        onSubmit={handleRegistrarMerma}
      />

      {/* Modal de Ajuste de Stock */}
      <ModalAjusteStock
        isOpen={showModalAjuste}
        onClose={handleCerrarModalAjuste}
        ingrediente={ingredienteParaAjuste}
        onConfirm={handleConfirmarAjuste}
        isLoading={ajustarStockMutation.isPending}
      />

      {/* Modal de Detalle de Orden de Compra */}
      {showDetalleOrden && (
        <DetalleOrdenCompra
          isOpen={showDetalleOrden}
          onClose={handleCerrarDetalleOrden}
          ordenId={ordenSeleccionadaId}
        />
      )}

      {/* Modal de Error */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={handleCerrarModalError}
        message={errorMessage}
        type={errorType}
        ingredientesSimilares={ingredientesSimilares}
        ingredienteExacto={ingredienteExacto}
        onReactivarIngrediente={(ingrediente) => {
          // Función para reactivar ingrediente desde el modal
          handleReactivarIngrediente(ingrediente, 1); // Stock mínimo para reactivar
          handleCerrarModalError();
          cerrarFormulario(); // Cerrar formulario después de reactivar
        }}
        onContinuarDeTodasFormas={handleContinuarDeTodasFormas}
      />
    </div>
  );
};

// Componente Modal para mostrar detalles del ingrediente
interface DetallesIngredienteModalProps {
  ingrediente: Ingrediente;
  isOpen: boolean;
  onClose: () => void;
  onVerOrdenCompra?: (ordenId: string) => void;
}

type TabKey = 'general' | 'movimientos' | 'proveedores';

const DetallesIngredienteModal: React.FC<DetallesIngredienteModalProps> = ({
  ingrediente,
  isOpen,
  onClose,
  onVerOrdenCompra,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  // Los hooks van todos antes del `if (!isOpen) return null` de más abajo:
  // llamarlos después sería una llamada condicional a hooks.
  const { hasPermission } = usePermissions();
  // Ambos bloques del modal usan permisos dinámicos: el historial de
  // movimientos depende de `ingredientes.ver` y la valorización manual de
  // `ingredientes.editar`.
  const puedeVerMovimientos = hasPermission(PERMISSIONS.INGREDIENTES_READ);
  const puedeEditarCosto = hasPermission(PERMISSIONS.INGREDIENTES_UPDATE);
  const updateMutation = useUpdateIngrediente();
  const { data: lotesData } = useLotesIngrediente(
    isOpen ? Number(ingrediente.id) : null
  );

  const [tabActivo, setTabActivo] = React.useState<TabKey>('general');
  const [editandoCosto, setEditandoCosto] = React.useState(false);
  const [costoInput, setCostoInput] = React.useState<string>('');

  React.useEffect(() => {
    if (isOpen) {
      setTabActivo('general');
      setEditandoCosto(false);
      setCostoInput(String(ingrediente.costoUnitario || 0));
    }
  }, [isOpen, ingrediente.id, ingrediente.costoUnitario]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);

  if (!isOpen) return null;

  const tieneLotesCosteados =
    !!lotesData && lotesData.totales.total_disponible_base > 0;
  const valorizacionLotes = lotesData?.totales.valorizacion_total ?? 0;
  const costoPromedioLotes = lotesData?.totales.costo_promedio_base ?? 0;
  const baseUnit = lotesData?.ingrediente.unidad_base_simbolo ?? '';
  const displayUnit =
    lotesData?.ingrediente.unidad_display_simbolo ??
    ingrediente.unidadMedida.abreviacion;

  // Costo unitario "efectivo": si hay lotes → costo promedio base, si no → costo_unitario
  // manual (que puede haberse definido vía sobreescritura o tomado del proveedor).
  const costoUnitarioDeclarado = Number(ingrediente.costoUnitario) || 0;
  const valorizacionManual =
    Number(ingrediente.stockActual || 0) * costoUnitarioDeclarado;
  const valorizacionEfectiva = tieneLotesCosteados
    ? valorizacionLotes
    : valorizacionManual;

  const guardarCostoManual = async (nuevoCosto: number) => {
    try {
      await updateMutation.mutateAsync({
        id: Number(ingrediente.id),
        data: { costoUnitario: nuevoCosto } as any,
      });
      toastSuccess('Costo unitario actualizado');
      setEditandoCosto(false);
    } catch (err: any) {
      toastError(
        err?.response?.data?.message || 'No se pudo actualizar el costo'
      );
    }
  };

  const tabs: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
    { key: 'general', label: 'General', icon: <Info className="h-4 w-4" /> },
    {
      key: 'movimientos',
      label: 'Movimientos & gráfico',
      icon: <TrendingDown className="h-4 w-4" />,
    },
    {
      key: 'proveedores',
      label: `Proveedores (${ingrediente.proveedores?.length || 0})`,
      icon: <Truck className="h-4 w-4" />,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg shrink-0">
              <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {ingrediente.nombre}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {ingrediente.marca && ingrediente.marca !== 'genérica' && (
                  <span className="font-medium">{ingrediente.marca} · </span>
                )}
                {ingrediente.categoria} · {ingrediente.unidadMedida.nombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
            title="Cerrar"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTabActivo(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tabActivo === t.key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tabActivo === 'general' && (
            <div className="space-y-6">
              {/* Tarjetas stock + valorización */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-start">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg shrink-0">
                      <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        Stock actual
                      </p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {ingrediente.stockActual} {displayUnit}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-start">
                    <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg shrink-0">
                      <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        Stock mínimo
                      </p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {ingrediente.stockMinimo} {displayUnit}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-start">
                    <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg shrink-0">
                      <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        Valorización
                      </p>
                      <p className="text-xl font-bold text-green-700 dark:text-green-400">
                        {formatCurrency(valorizacionEfectiva)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {tieneLotesCosteados
                          ? `${formatCurrency(costoPromedioLotes)} / ${baseUnit} (FIFO)`
                          : `${formatCurrency(costoUnitarioDeclarado)} / ${displayUnit} (manual)`}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-start">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg shrink-0">
                      <Truck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        Proveedores
                      </p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {ingrediente.proveedores?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sobreescritura de valorización cuando no hay facturas */}
              {puedeEditarCosto && !tieneLotesCosteados && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 border-blue-400 border-t border-r border-b border-gray-200/50 dark:border-gray-700/50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg shrink-0">
                      <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Valorización manual
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        No hay lotes cargados todavía. Puedes definir el costo
                        unitario manualmente o importarlo del proveedor
                        principal para estimar la valorización del stock.
                      </p>
                      {!editandoCosto ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Costo actual:{' '}
                            <strong>
                              {formatCurrency(costoUnitarioDeclarado)} /{' '}
                              {displayUnit}
                            </strong>
                          </span>
                          <button
                            onClick={() => setEditandoCosto(true)}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 hover:bg-blue-200 text-blue-800 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300 transition-colors"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Editar costo
                          </button>
                          {ingrediente.proveedores &&
                            ingrediente.proveedores.length > 0 && (
                              <button
                                onClick={() => {
                                  const precio =
                                    ingrediente.proveedores![0].precioUnitario;
                                  guardarCostoManual(precio);
                                }}
                                disabled={updateMutation.isPending}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-300 transition-colors disabled:opacity-50"
                              >
                                <Truck className="h-3 w-3 mr-1" />
                                Usar precio del proveedor (
                                {formatCurrency(
                                  ingrediente.proveedores[0].precioUnitario
                                )}
                                )
                              </button>
                            )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">
                              $
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={costoInput}
                              onChange={(e) => setCostoInput(e.target.value)}
                              className="pl-7 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                              autoFocus
                            />
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            / {displayUnit}
                          </span>
                          <button
                            onClick={() =>
                              guardarCostoManual(parseFloat(costoInput) || 0)
                            }
                            disabled={updateMutation.isPending}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditandoCosto(false);
                              setCostoInput(
                                String(ingrediente.costoUnitario || 0)
                              );
                            }}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Descripción */}
              {ingrediente.descripcion && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    Descripción
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {ingrediente.descripcion}
                  </p>
                </div>
              )}

              {/* Lotes activos */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  Lotes en bodega
                </h3>
                <IngredienteLotesPanel
                  ingredienteId={Number(ingrediente.id)}
                />
              </div>
            </div>
          )}

          {tabActivo === 'movimientos' && (
            <div>
              {puedeVerMovimientos ? (
                <TablaMovimientosStock
                  ingredienteId={parseInt(ingrediente.id)}
                  onVerOrdenCompra={onVerOrdenCompra}
                />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  El historial de movimientos y gráfico solo está disponible
                  para administradores.
                </div>
              )}
            </div>
          )}

          {tabActivo === 'proveedores' && (
            <div>
              {ingrediente.proveedores && ingrediente.proveedores.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ingrediente.proveedores.map((proveedor) => (
                    <div
                      key={proveedor.id}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg shrink-0">
                          <Truck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {proveedor.proveedor.nombre}
                          </h4>
                          {proveedor.proveedor.contacto && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {proveedor.proveedor.contacto}
                            </p>
                          )}
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center text-sm">
                              <DollarSign className="h-4 w-4 mr-1 text-green-600 dark:text-green-400" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatCurrency(proveedor.precioUnitario)} /{' '}
                                {displayUnit}
                              </span>
                            </div>
                            <div className="flex items-center text-sm">
                              <Clock className="h-4 w-4 mr-1 text-blue-600 dark:text-blue-400" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {proveedor.tiempoEntrega} días de entrega
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Este ingrediente todavía no tiene proveedores asociados.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Modal para mostrar errores
interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type?: 'simple' | 'similar' | 'exacto' | 'exactoActivo';
  ingredientesSimilares?: any[];
  ingredienteExacto?: any | null;
  onReactivarIngrediente?: (ingrediente: any) => void;
  onContinuarDeTodasFormas?: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  message,
  type = 'simple',
  ingredientesSimilares = [],
  ingredienteExacto = null,
  onReactivarIngrediente,
  onContinuarDeTodasFormas,
}) => {
  if (!isOpen) return null;

  const esSimilar = type === 'similar' && ingredientesSimilares.length > 0;
  const esExacto = type === 'exacto' && ingredienteExacto;
  const esExactoActivo = type === 'exactoActivo' && ingredienteExacto;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {esSimilar
                ? 'Ingredientes Similares Encontrados'
                : esExacto
                  ? 'Ingrediente Existente (Inactivo)'
                  : esExactoActivo
                    ? 'Ingrediente Ya Existe'
                    : 'Error'}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
            {message}
          </p>

          {esSimilar && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Los siguientes ingredientes son similares al que intentas crear:
              </p>

              <div className="space-y-3">
                {ingredientesSimilares.map((ingrediente) => (
                  <div
                    key={ingrediente.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {ingrediente.nombre}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Categoría: {ingrediente.categoria}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Stock: {ingrediente.stock_actual}{' '}
                          {ingrediente.unidad_medida?.abreviacion}
                        </p>
                        <p
                          className={`text-sm font-medium ${
                            ingrediente.activo
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {ingrediente.activo ? 'Activo' : 'Inactivo'}
                        </p>
                      </div>

                      {!ingrediente.activo && onReactivarIngrediente && (
                        <button
                          onClick={() => onReactivarIngrediente(ingrediente)}
                          className="ml-4 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors duration-200"
                        >
                          Reactivar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Sugerencia:</strong> Puedes reactivar un ingrediente
                  inactivo o continuar creando uno nuevo si realmente necesitas
                  un ingrediente diferente.
                </p>
              </div>
            </div>
          )}

          {esExacto && ingredienteExacto && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ya existe un ingrediente con el mismo nombre:
              </p>

              <div className="border border-orange-200 dark:border-orange-600 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {ingredienteExacto.nombre}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Categoría: {ingredienteExacto.categoria}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Stock: {ingredienteExacto.stock_actual}{' '}
                      {ingredienteExacto.unidad_medida?.abreviacion}
                    </p>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">
                      Estado: Inactivo
                    </p>
                  </div>

                  {onReactivarIngrediente && (
                    <button
                      onClick={() => onReactivarIngrediente(ingredienteExacto)}
                      className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Reactivar
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  <strong>Recomendación:</strong> Te sugerimos reactivar el
                  ingrediente existente en lugar de crear uno nuevo para
                  mantener el historial y evitar duplicados.
                </p>
              </div>
            </div>
          )}

          {esExactoActivo && ingredienteExacto && (
            <div className="space-y-4">
              <div className="border border-red-200 dark:border-red-600 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {ingredienteExacto.nombre}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Categoría: {ingredienteExacto.categoria}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Stock: {ingredienteExacto.stock_actual}{' '}
                      {ingredienteExacto.unidad_medida?.abreviacion}
                    </p>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      Estado: Activo
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>No se puede crear:</strong> Ya existe un ingrediente
                  activo con este nombre. No es posible tener duplicados activos
                  en el sistema.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          {esSimilar ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Volver
              </button>
              <button
                onClick={onContinuarDeTodasFormas}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Continuar de Todas Formas
              </button>
            </>
          ) : esExacto ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Volver
              </button>
              <button
                onClick={onContinuarDeTodasFormas}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Crear de Todas Formas
              </button>
            </>
          ) : esExactoActivo ? (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Entendido
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aceptar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ingredientes;
